import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, LogOut, Copy, Check, Download, Send, ExternalLink } from "lucide-react";
import { useNoIndex } from "../lib/seoHelpers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * /admin/reindex — helper for the "request re-indexing" workflow.
 *
 * Two paths:
 *   1. Google Search Console: no public API for URL Inspection. Admin pastes
 *      each URL manually into GSC → URL Inspection → Request Indexing.
 *      We give them a copy-friendly list and a CSV download.
 *   2. IndexNow (Bing/Yandex/Seznam): actual API. Admin generates a key,
 *      hosts /{key}.txt at the site root, then hits this page's Ping button
 *      to submit all 55 URLs in one shot.
 */
export default function AdminReindexPage() {
  useNoIndex();
  const navigate = useNavigate();
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedAll, setCopiedAll] = useState(false);
  const [indexnowKey, setIndexnowKey] = useState("");
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("sdc_admin_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("sdc_admin_token")) {
      navigate("/admin");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/reindex-urls`, {
          headers: getAuthHeaders(),
        });
        setUrls(res.data.urls || []);
      } catch (e) {
        if (e.response?.status === 401) {
          navigate("/admin");
          return;
        }
        setError("Failed to load URL list.");
      } finally {
        setLoading(false);
      }
    })();
  }, [getAuthHeaders, navigate]);

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(urls.join("\n"));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (_e) {
      // Silent
    }
  };

  const handleDownloadCsv = () => {
    const csv = "url\n" + urls.map((u) => `"${u.replace(/"/g, '""')}"`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `sdc-reindex-urls-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handlePing = async () => {
    if (!indexnowKey.trim()) {
      setPingResult({ ok: false, msg: "Enter your IndexNow key first." });
      return;
    }
    setPinging(true);
    setPingResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/indexnow-ping`,
        { key: indexnowKey.trim() },
        { headers: getAuthHeaders() }
      );
      setPingResult({
        ok: res.data.success,
        msg: `IndexNow status ${res.data.status_code} — submitted ${res.data.submitted} URLs.${res.data.response_text ? " " + res.data.response_text : ""}`,
      });
    } catch (e) {
      setPingResult({
        ok: false,
        msg: e.response?.data?.detail || "IndexNow request failed. Verify your key file is live at https://socialdisputeco.com/{key}.txt.",
      });
    } finally {
      setPinging(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sdc_admin_token");
    localStorage.removeItem("sdc_admin_user");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]" data-testid="admin-reindex-page">
      <header className="bg-[#252825] border-b border-[#3f4816]/50 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-[#888680] hover:text-[#d9fb06]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg md:text-xl font-black text-[#d9fb06] uppercase">Re-indexing Helper</h1>
              <p className="text-xs text-[#888680] font-mono">Push the 55 previously-broken URLs to Google &amp; Bing</p>
            </div>
          </div>
          <button onClick={handleLogout} className="text-[#888680] hover:text-red-400 flex items-center gap-1.5 text-sm">
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-10 py-10 space-y-8">
        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>
        )}

        {/* Google Search Console */}
        <section className="rounded-2xl border border-[#3f4816]/50 bg-[#252825] p-6" data-testid="reindex-gsc-section">
          <h2 className="text-xl font-black text-[#d9fb06] uppercase mb-2">1. Google Search Console</h2>
          <p className="text-sm text-[#888680] mb-4">
            Google has no bulk-reindex API. Paste each URL into{" "}
            <a
              href="https://search.google.com/search-console"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#d9fb06] hover:underline inline-flex items-center gap-1"
            >
              Search Console <ExternalLink className="w-3 h-3" />
            </a>{" "}
            → URL Inspection → Request Indexing. Rate-limited to ~10-15 per day; prioritize the appeal-matrix URLs first.
          </p>
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={handleCopyAll}
              disabled={urls.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90 disabled:opacity-50"
              data-testid="reindex-copy-all-btn"
            >
              {copiedAll ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedAll ? `Copied ${urls.length} URLs` : `Copy all ${urls.length} URLs`}
            </button>
            <button
              onClick={handleDownloadCsv}
              disabled={urls.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#302f2c] border border-[#3f4816] text-[#d9fb06] text-sm font-bold hover:bg-[#3f4816]/40 disabled:opacity-50"
              data-testid="reindex-download-csv-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Download CSV
            </button>
          </div>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[#d9fb06] mx-auto" />
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto rounded-lg border border-[#3f4816]/40 bg-[#1a1c1b] p-3">
              <ol className="text-xs font-mono text-[#dfddd6] space-y-1" data-testid="reindex-url-list">
                {urls.map((url, i) => (
                  <li key={url} className="flex items-start gap-3">
                    <span className="text-[#888680] w-8 flex-shrink-0 text-right">{i + 1}.</span>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-[#d9fb06] break-all">
                      {url}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>

        {/* IndexNow */}
        <section className="rounded-2xl border border-[#3f4816]/50 bg-[#252825] p-6" data-testid="reindex-indexnow-section">
          <h2 className="text-xl font-black text-[#d9fb06] uppercase mb-2">2. IndexNow (Bing, Yandex, Seznam, Naver)</h2>
          <p className="text-sm text-[#888680] mb-4">
            One API call submits all {urls.length || 55} URLs at once. Steps:
          </p>
          <ol className="text-sm text-[#dfddd6] space-y-2 mb-5 list-decimal list-inside">
            <li>
              Generate a random alphanumeric key (32+ chars) at{" "}
              <a
                href="https://www.bing.com/indexnow/getstarted"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#d9fb06] hover:underline inline-flex items-center gap-1"
              >
                bing.com/indexnow/getstarted <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>
              Host a file at <code className="text-[#d9fb06]">https://socialdisputeco.com/{"{key}"}.txt</code> containing
              only the key string. (Add it to <code>/app/frontend/public/</code>.)
            </li>
            <li>Paste the key below and click Ping.</li>
          </ol>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              type="text"
              value={indexnowKey}
              onChange={(e) => setIndexnowKey(e.target.value)}
              placeholder="Your IndexNow key (e.g. a1b2c3d4e5f6...)"
              className="flex-1 bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] outline-none font-mono"
              data-testid="reindex-indexnow-key"
            />
            <button
              onClick={handlePing}
              disabled={pinging || !indexnowKey.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90 disabled:opacity-50 justify-center"
              data-testid="reindex-indexnow-ping-btn"
            >
              {pinging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Ping IndexNow
            </button>
          </div>
          {pingResult && (
            <div
              className={`mt-4 rounded-lg border p-3 text-sm ${pingResult.ok ? "border-green-500/40 bg-green-500/10 text-green-300" : "border-red-500/40 bg-red-500/10 text-red-300"}`}
              data-testid="reindex-indexnow-result"
            >
              {pingResult.msg}
            </div>
          )}
          <p className="text-xs text-[#888680] mt-4">
            Bing typically recrawls within 24 hours after a successful IndexNow ping. Google does not participate in
            IndexNow — use Search Console for Google.
          </p>
        </section>
      </main>
    </div>
  );
}
